import { useMutation, useQuery } from '@tanstack/react-query'

import type { Detail } from '../orders/orders-types'

import { itemsService } from './items-services'
import type { ItemPayload, ItemsQueryParams } from './items-types'
import { formatToTitleCase } from '@/utils/text'

type ItemMutationPayload = Partial<Omit<ItemPayload, 'order' | 'origin_item'>>

type ItemMutationAction = keyof ItemMutationPayload

export const useItemMutation = ({
  action,
  withoutInvalidation
}: {
  action: ItemMutationAction
  withoutInvalidation?: boolean
}) => {
  const updateItemMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ItemMutationPayload }) =>
      itemsService.update(id, payload),
    meta: {
      successMessage: `${formatToTitleCase(action)} updated successfully`,
      invalidatesQuery: withoutInvalidation ? [] : ['orders', 'timeline-orders']
    }
  })

  const addItemMutation = useMutation({
    mutationFn: ({ payload, detail }: { payload: ItemMutationPayload; detail: Detail }) =>
      itemsService.add({
        ...payload,
        order: detail?.doc_aid,
        origin_item: detail?.autoid
      }),
    meta: {
      successMessage: `${formatToTitleCase(action)} updated successfully`,
      invalidatesQuery: withoutInvalidation ? [] : ['orders', 'timeline-orders']
    }
  })

  const handleMutation = async ({ detail, payload }: { payload: ItemMutationPayload; detail: Detail }) => {
    if (detail?.item?.id) {
      updateItemMutation.mutateAsync({
        id: detail.item.id,
        payload
      })
    } else {
      addItemMutation.mutateAsync({
        detail,
        payload
      })
    }
  }

  return { handleMutation, isPending: addItemMutation.isPending || updateItemMutation.isPending }
}

export const useItems = (params: ItemsQueryParams = {}) => {
  return useQuery({
    queryKey: ['items', params != null ? Object.values(params) : []],
    queryFn: () => itemsService.get(params ?? {})
  })
}

export const useItemsByAutoIds = (autoids: string[]) => {
  const sorted = [...autoids].sort()
  const key = sorted.join(',')
  return useQuery({
    queryKey: ['items', 'by-autoids', key],
    queryFn: () => itemsService.get({ autoid__in: key, limit: Math.max(sorted.length, 100) }),
    enabled: sorted.length > 0,
    staleTime: 30_000
  })
}
